import { useCallback, useEffect, useRef } from 'react'

import { EDITOR_DRAG_DETECTION_THRESHOLD } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { TOAST_CONFIG } from '@editor/config/styles'
import { useEditorContext } from '@editor/contexts/Editor'
import { PointArray } from '@editor/interfaces/attribute'
import { PolygonPlaneMeshProps } from '@editor/interfaces/canvas'
import { ShapeKeyType } from '@editor/interfaces/shape'
import { Tools } from '@editor/pages/projects/editor/tools/setup'
import { validateSelectedShape } from '@editor/services/Util'
import { getVolumeEstimationItems } from '@editor/services/VolumeEstimation'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { setCursor, setEditingElementId, setHoveredElementId, setSelectedElementIds } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import { ThreeEvent } from '@react-three/fiber'
import { useMatches } from 'react-router'
import { Vector2 } from 'three'

export const TOAST_ID = 'shape-selection-error'

const useEventHandlers = ({ id, polygon: shape, vertexAnchors, movable, selectable }: PolygonPlaneMeshProps) => {
  // Editor related variables
  const { changeSelectedShapeIds } = useEditorContext()

  // Route matching to check current page
  const matches = useMatches()
  const isEditorPage = matches.some((row) => row.id === 'project-editor')
  const isBlueprintPage = matches.some((row) => row.id === 'project-blueprint-editor')

  // Store
  const dispatch = useAppDispatch()
  const shapes = useAppSelector((state) => state.shapes.shapes)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)
  const editingElementId = useAppSelector((state) => state.toolUtil.core.editingElementId)
  const selectedElementIds = useAppSelector((root) => root.toolUtil.core.selectedElementIds)

  // Refs
  const isMouseInsideRef = useRef(false)
  const isDraggingRef = useRef(false)
  const dragStartPosRef = useRef<PointArray | null>(null)
  const currentPositionsRef = useRef<PointArray[] | null>(null)

  const movingStartLocalPointRef = useRef<Vector2 | null>(null)
  const tempV2Ref = useRef(new Vector2())

  const currentEditorTool = Tools.find((tool) => tool.key === selectedTool)

  /**
   * Handle mouse enter event on the polygon plane mesh.
   *
   * @param ev - The mouse event.
   */
  const handleMouseEnter = (ev: ThreeEvent<MouseEvent>) => {
    // don't allow hovering if editing anything
    if (editingElementId) return

    isMouseInsideRef.current = true
    ev.stopPropagation()
    if (movable) dispatch(setCursor(CursorState.MOVE))
    else if (selectable) dispatch(setCursor(CursorState.POINTER))
    if (selectable) dispatch(setHoveredElementId(id))
  }

  /**
   * Handle mouse leave event on the polygon plane mesh.
   *
   * @param ev - The mouse event.
   */
  const handleMouseLeave = (ev: ThreeEvent<MouseEvent>) => {
    ev.stopPropagation()
    isMouseInsideRef.current = false
    if (movable && !isDraggingRef.current) dispatch(setCursor(CursorState.DEFAULT))
    else if (selectable) dispatch(setCursor(CursorState.DEFAULT))
    if (selectable) dispatch(setHoveredElementId(''))
  }

  /**
   * Handle mouse down event on the polygon plane mesh.
   *
   * @param ev - The mouse event.
   */
  const handleMouseDown = (ev: ThreeEvent<MouseEvent>) => {
    ev.stopPropagation()
    movingStartLocalPointRef.current = new Vector2(ev.clientX, ev.clientY)
  }

  const doSelection = useCallback(() => {
    if (!isEditorPage) {
      dispatch(setSelectedElementIds([id]))
      return
    }

    try {
      validateSelectedShape(shape, ShapeKeyType.POLYGON, getVolumeEstimationItems(inspectionItems), currentEditorTool)
    } catch (e) {
      if (!toaster.isVisible(TOAST_ID)) {
        toaster.create({
          ...TOAST_CONFIG,
          id: TOAST_ID,
          type: 'error',
          title: e instanceof Error ? e.message : 'エラー',
        })
      }
      return
    }

    let newShapeIds = [...selectedElementIds]

    // If only 1 volume can be selected, deselect all other volumes
    if (currentEditorTool && currentEditorTool.config?.volume?.onlyOneSelectable) {
      newShapeIds = newShapeIds
        .filter((i) => !shapes.polygons.find((poly) => poly.shape_id === i))
        .filter((i) => !shapes.cylinders.find((cylinder) => cylinder.shape_id === i))
    }

    const idIndex = newShapeIds.indexOf(shape.shape_id)
    if (idIndex >= 0) {
      newShapeIds.splice(idIndex, 1)
    } else {
      newShapeIds.push(shape.shape_id)
    }

    changeSelectedShapeIds(newShapeIds)
  }, [
    isEditorPage,
    id,
    shape,
    selectedElementIds,
    shapes,
    inspectionItems,
    currentEditorTool,
    dispatch,
    changeSelectedShapeIds,
  ])

  /**
   * Handle click event on the polygon plane mesh.
   *
   * @param ev - The mouse event.
   */
  const handleClick = (ev: ThreeEvent<MouseEvent>) => {
    if (isDraggingRef.current) return

    ev.stopPropagation()
    doSelection()
  }

  /**
   * Handle mouse up event on the polygon plane mesh.
   * This is registered on window to capture events outside the polygon and canvas
   * if user's mouse goes out of bounds while dragging.
   */
  useEffect(() => {
    const handleMouseUp = (ev: MouseEvent) => {
      movingStartLocalPointRef.current = null

      if (!isDraggingRef.current) return
      ev.stopPropagation()

      if (currentPositionsRef.current) vertexAnchors?.onUpdated?.(currentPositionsRef.current)
      dispatch(setEditingElementId(undefined))
      if (!isMouseInsideRef.current) dispatch(setCursor(CursorState.DEFAULT))
      if (isBlueprintPage) doSelection()

      isDraggingRef.current = false
      dragStartPosRef.current = null
      currentPositionsRef.current = null
    }

    const handleMouseMove = (ev: MouseEvent) => {
      if (movingStartLocalPointRef.current) {
        const distance = movingStartLocalPointRef.current.distanceTo(tempV2Ref.current.set(ev.clientX, ev.clientY))
        if (distance > EDITOR_DRAG_DETECTION_THRESHOLD && !isDraggingRef.current) {
          isDraggingRef.current = true
          dispatch(setEditingElementId(id))
          dispatch(setHoveredElementId(''))
        }
      }
    }

    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [dispatch, doSelection, isBlueprintPage, vertexAnchors, id])

  return {
    dragStartPosRef,
    currentPositionsRef,
    isDraggingRef,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleClick,
  }
}

export default useEventHandlers
